
import { useState } from 'react';
import type { AceFaqItem } from '@/lib/aces-data';
import { cn } from '@/lib/utils';
import { ChevronDown, HelpCircle } from 'lucide-react';

type FAQAccordionProps = {
  items: AceFaqItem[];
  className?: string;
};

export function FAQAccordion({ items, className }: FAQAccordionProps) {
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);

  if (!items.length) {
    return (
      <p className="font-inter text-sm text-gray-500">
        Nenhuma pergunta frequente disponível para este Ace.
      </p>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {items.map((item) => {
        const isOpen = openId === item.id;
        return (
          <div
            key={item.id}
            className={cn(
              'overflow-hidden rounded-2xl border border-gray-200 bg-white transition-colors duration-200',
              isOpen && 'border-amber-200/80 shadow-sm'
            )}
          >
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : item.id)}
              className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50/80"
              aria-expanded={isOpen}
            >
              <span className="flex min-w-0 items-start gap-3">
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span className="font-poppins text-sm font-semibold text-gray-900 md:text-base">
                  {item.question}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300',
                  isOpen && 'rotate-180'
                )}
              />
            </button>

            <div
              className={cn(
                'grid transition-all duration-300 ease-in-out',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
            >
              <div className="overflow-hidden">
                <p className="border-t border-gray-100 px-5 py-4 pl-12 font-inter text-sm leading-relaxed text-gray-600">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
